import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useCookieConsent } from '../context/CookieConsentContext';
import ProductCard from '../components/ProductCard';
import { calculateTotal } from '../functions/calculateTotal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faClock, faLock } from '@fortawesome/free-solid-svg-icons';
import SEO from '../components/SEO';
import ExpressCheckout from '../components/ExpressCheckout';
import TrustBadge from '../components/TrustBadge';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../context/LocaleContext';
import { trackInitiateCheckout, generateEventId, getMetaCookies } from '../components/MetaPixel';
import { getUtmForCheckout, getUtmForCapi } from '../utils/utm';

const Cart: React.FC = () => {
  const { items, email } = useCart();
  const { consent } = useCookieConsent();
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const [showMadeToOrderModal, setShowMadeToOrderModal] = useState(false);
  const total = calculateTotal(items);
  const hasOneOfKindItem = items.some(item => item.oneOfKind);
  const hasMadeToOrderItem = items.some(item => item.madeToOrder);
  const FREE_SHIPPING_THRESHOLD = 200;
  const remainingForFreeShipping = FREE_SHIPPING_THRESHOLD - total;

  const handleCheckout = async () => {
    // Track InitiateCheckout
    const eventId = generateEventId();
    trackInitiateCheckout({
      content_ids: items.map(i => i.id),
      contents: items.map(i => ({ id: i.id, quantity: i.quantity })),
      value: total,
      currency: 'EUR',
    }, consent.marketing, eventId);

    const { fbp, fbc } = getMetaCookies();
    fetch('/api/notify?action=meta-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventName: 'InitiateCheckout',
        eventId,
        userData: { fbp, fbc },
        customData: {
          content_ids: items.map(i => i.id),
          value: total,
          currency: 'EUR',
          ...getUtmForCapi(),
        },
      }),
    }).catch(() => {});

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items.map(item => ({
            ...item,
            name: item.madeToOrder ? `${item.name} (Made to Order - 1-2 weeks)` : item.name,
          })),
          utm: getUtmForCheckout(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Network response was not ok');
      }

      const { url } = await response.json();
      if (!url) throw new Error('No checkout URL returned');

      window.location.href = url;
    } catch (error) {
      console.error('Checkout error:', error);
    }
  };

  const handleProceedToCheckout = () => {
    if (hasMadeToOrderItem) {
      setShowMadeToOrderModal(true);
    } else {
      handleCheckout();
    }
  };

  const handleConfirmCheckout = () => {
    setShowMadeToOrderModal(false);
    handleCheckout();
  };

  if (items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-[90vw] md:max-w-[min(80vw,1400px)] text-center">
        <h1 className="text-3xl font-bold text-brand-dark dark:text-brand-white mb-8">
          {t('cart.empty')}
        </h1>
        <p className="text-brand-dark dark:text-brand-white mb-8">
          {t('cart.emptyMessage')}
        </p>
        <Link
          to={localePath('/shop')}
          className="inline-block bg-brand-blue text-white px-6 py-3 rounded-lg hover:bg-brand-dark transition-colors"
        >
          {t('cart.continueShopping')}
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={t('seo.cart.title')}
        description={t('seo.cart.description')}
        noindex={true}
      />
      <div className="container mx-auto px-4 py-8 max-w-[90vw] md:max-w-[min(80vw,1400px)]">
        <h1 className="text-3xl font-bold text-brand-dark dark:text-brand-white mb-8">
          {t('cart.title')}
        </h1>

        <div className="mb-8 p-4 bg-brand-blue/10 rounded-lg">
          {total >= FREE_SHIPPING_THRESHOLD ? (
            <p className="text-sm text-brand-dark dark:text-brand-white flex items-center gap-2">
              <span className="text-brand-blue">✨</span>
              {t('cart.freeShipping')}
            </p>
          ) : (
            <p className="text-sm text-brand-dark dark:text-brand-white flex items-center gap-2">
              <span className="text-brand-blue">🚚</span>
              {t('cart.freeShippingRemaining', { amount: remainingForFreeShipping.toFixed(2) })}
            </p>
          )}
        </div>

        {hasOneOfKindItem && (
          <div className="mb-8 p-4 bg-brand-blue/10 rounded-lg flex items-start gap-2">
            <FontAwesomeIcon icon={faStar} className="text-brand-blue mt-1" />
            <p className="text-sm text-brand-dark dark:text-brand-white">
              {t('cart.oneOfKindNotice')}
            </p>
          </div>
        )}

        {hasMadeToOrderItem && (
          <div className="mb-8 p-4 bg-amber-500/10 rounded-lg flex items-start gap-2">
            <FontAwesomeIcon icon={faClock} className="text-amber-500 mt-1" />
            <p className="text-sm text-brand-dark dark:text-brand-white">
              {t('cart.madeToOrderNotice')}
            </p>
          </div>
        )}
        
        <div className="grid gap-6 mb-8">
          {items.map(item => (
            <ProductCard key={item.id} {...item} />
          ))}
        </div>

        <div className="mb-8">
          <TrustBadge variant="detailed" />
        </div>

        <div className="bg-white dark:bg-brand-dark p-6 rounded-lg shadow-md">
          <div className="flex justify-between items-center mb-6">
            <span className="text-xl font-semibold text-brand-dark dark:text-brand-white">
              {t('cart.total')}
            </span>
            <span className="text-2xl font-bold text-brand-dark dark:text-brand-white">
              €{total.toFixed(2)}
            </span>
          </div>

          <ExpressCheckout />

          <div className="flex gap-4">
            <Link
              to={localePath('/shop')}
              className="flex-1 px-6 py-3 text-center bg-gray-200 dark:bg-brand-charcoal text-brand-dark dark:text-brand-white rounded-lg hover:bg-gray-300 dark:hover:bg-brand-dark transition-colors"
            >
              {t('cart.continueShopping')}
            </Link>
            <button
              onClick={handleProceedToCheckout}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-brand-blue text-white rounded-lg hover:bg-brand-dark transition-colors"
            >
              <FontAwesomeIcon icon={faLock} className="text-xs" />
              {t('cart.checkout')}
            </button>
          </div>
          <TrustBadge variant="compact" />
        </div>

        {/* Made-to-Order Confirmation Modal */}
        <AnimatePresence>
          {showMadeToOrderModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
              onClick={() => setShowMadeToOrderModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white dark:bg-gray-900 rounded-xl p-6 max-w-md w-full shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/50 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faClock} className="text-amber-500 text-xl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {t('cart.madeToOrderTitle')}
                  </h3>
                </div>

                <div className="space-y-3 mb-6">
                  <p className="text-gray-700 dark:text-gray-300">
                    {t('cart.madeToOrderMessage')}
                  </p>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <ul className="space-y-2 text-sm text-amber-800 dark:text-amber-200">
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">⏱</span>
                        <span>{t('cart.madeToOrderProduction')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">📦</span>
                        <span>{t('cart.madeToOrderShipping')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">✨</span>
                        <span>{t('cart.madeToOrderCare')}</span>
                      </li>
                    </ul>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('cart.madeToOrderContact')}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowMadeToOrderModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    {t('cart.goBack')}
                  </button>
                  <button
                    onClick={handleConfirmCheckout}
                    className="flex-1 px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-dark transition-colors"
                  >
                    {t('cart.understandCheckout')}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default Cart;